import { isDayLightSavingMode } from '@ppwr/poker'
import { convertUTCDataToUTCDate } from '@ppwr/sdk'
interface FormatDateProps {
  showYear?: boolean
  showTime?: boolean
}
const formatDate = (date: Date, options: FormatDateProps) => {
  const { showYear, showTime } = options

  // Format the month
  const formatter = new Intl.DateTimeFormat('en', { month: 'short' })
  const month = formatter.format(date)

  // Extract day, year, hours, and minutes
  const day = date.getDate()
  const year = date.getFullYear()
  const hours = date.getHours()
  const minutes = date.getMinutes()

  // Format minutes with leading zero if needed
  const formattedMinutes = minutes.toString().padStart(2, '0')

  // Construct the date string
  let formattedDate = `${month} ${day}${showYear ? `, ${year}` : ''}`

  if (showTime) {
    // Format hours to 12-hour format
    const hour12 = hours % 12 || 12
    const period = hours >= 12 ? 'PM' : 'AM'
    formattedDate += `, ${hour12}:${formattedMinutes} ${period}`
  }

  return formattedDate
}

export const formatPlayDate = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const formatShortMonthAndDate = (date: Date) => {
  const formatter = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' })
  return formatter.format(date)
}

export const getMonthName = (date: Date) => {
  // Get the month name from the date object
  const monthName = date.toLocaleString('default', { month: 'long' })
  // Return the first letter of the month name
  return monthName.slice(undefined, 3)
}

export const getDayName = (date: Date) => {
  // Get the month name from the date object
  const monthName = date.toLocaleString('default', { weekday: 'short' })
  // Return the first letter of the month name
  return monthName.slice(undefined, 3)
}

export const formatDatePicker = (date: Date) => {
  const capitalizeFirstLetter = (string: string): string => {
    if (!string) return ''
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  return `${capitalizeFirstLetter(getDayName(date))} ${capitalizeFirstLetter(getMonthName(date))} ${date.getDate()}`
}

export const formatTimePicker = (date: Date, minute?: 'numeric' | '2-digit', timeZone?: string) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  const hasTimezone = dateObj.toLocaleString('en-US', {
    hour: 'numeric',
    minute: minute ? minute : 'numeric',
    hour12: true,
    ...(timeZone ? { timeZone } : {})
  })

  return hasTimezone
}

export const formatGameScheduledAt = (date: Date) => {
  return `${getMonthName(date)} ${date.getDate()}, ${date.getFullYear()} at ${formatTimePicker(date)}`
}

/**
 * Formats a date to 'MMM DD, YYYY' format
 * @param createdAt - date string
 * @returns  'MMM DD, YYYY'
 * @example
 * shortDateFormatter('2021-10-01T00:00:00.000Z') // 'Oct 1, 2021'
 * shortDateFormatter('2021-10-10T00:00:00.000Z') // 'Oct 10, 2021'
 */
export const shortDateFormatter = (createdAt: string) => {
  return formatDate(new Date(createdAt), { showYear: true })
}

/**
 * Formats a date to 'MMM DD' format
 * @param date - date string
 * @returns 'MMM DD'
 * @example
 * monthAndDayFormatter('2021-10-01T00:00:00.000Z') // 'Oct 1'
 * monthAndDayFormatter('2021-10-10T00:00:00.000Z') // 'Oct 10'
 */
export const monthAndDayFormatter = (date: Date) => {
  return formatDate(date, { showYear: false })
}

/**
 * Formats a date to 'MMM DD, HH:MM AM/PM PST' format
 * @param date - date string
 * @returns 'MMM DD, HH:MM AM/PM PST'
 * @example
 * monthDayAndTimeFormatter('2021-10-01T00:00:00.000Z') // 'Oct 1, 12:00 AM PST'
 * monthDayAndTimeFormatter('2021-10-10T00:00:00.000Z') // 'Oct 10, 12:00 AM PST'
 * monthDayAndTimeFormatter('2021-10-10T12:00:00.000Z') // 'Oct 10, 12:00 PM PST'
 * */
export const monthDayAndTimeFormatter = (date: Date) => {
  return formatDate(date, { showYear: false, showTime: true })
}

export const monthDayAndTimeFormatterWithYear = (date: Date) => {
  return formatDate(date, { showYear: true, showTime: true })
}

/**
 *
 * @returns Date object for tomorrow at 6 AM local time
 */
export function getLocalTimeForTomorrowAtSix() {
  const now = new Date()

  // Get the current local timezone offset in minutes
  const currentOffsetMinutes = now.getTimezoneOffset()

  // Create a new Date object for tomorrow at the current time
  const tomorrow = new Date(now)
  tomorrow.setUTCDate(now.getUTCDate() + 1)
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  if (timeZone === 'America/New_York' && isDayLightSavingMode(tomorrow)) {
    tomorrow.setUTCHours(5, 0, 0, 0) // Set to 6 AM UTC
  } else {
    tomorrow.setUTCHours(6, 0, 0, 0) // Set to 6 AM UTC
  }

  // Get the local timezone offset in minutes for tomorrow at 6 AM UTC
  const tomorrowOffsetMinutes = tomorrow.getTimezoneOffset()

  // Check if the timezone offset changes, indicating a DST change
  const offsetDifferenceMinutes = tomorrowOffsetMinutes - currentOffsetMinutes

  // Adjust the time if there's a difference due to DST change
  if (offsetDifferenceMinutes !== 0) {
    tomorrow.setMinutes(tomorrow.getMinutes() - offsetDifferenceMinutes)
  }

  return tomorrow
}

export function formatScheduleTime(scheduleTime: string | undefined) {
  const date = scheduleTime ? new Date(scheduleTime) : new Date()
  const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase()
  const day = date.getDate()
  const dayName = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date)
  const time = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })

  return { month, day, dayName, time }
}

/** Format a public_daily schedule's UTC time fields as a localized clock string (e.g. "08:30 PM"). */
export function formatScheduleLocalTime(
  startDayOfWeekUTC: number | undefined,
  startHoursUTC: number | undefined,
  startMinutesUTC: number | undefined
): string {
  const localTime = convertUTCDataToUTCDate(startDayOfWeekUTC ?? 0, startHoursUTC ?? 0, startMinutesUTC ?? 0)
  return localTime.toLocaleTimeString([], { hour12: true, hour: '2-digit', minute: '2-digit' })
}

// export const getSecondsLeftFromUtcTime = (utcTime: number) => {
//   return utcTime - Timing.getUtcMsNow()
// }

/**
 * Formats a Unix timestamp (ms) as a human-readable chat header label.
 * Examples: "Tonight at 10:23 PM", "Yesterday at 3:45 PM", "Monday at 11:00 AM", "Jan 15 at 2:30 PM"
 */
export function formatChatTimestamp(ts: number): string {
  const date = new Date(ts)
  const now = new Date()
  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  const isToday = date.toDateString() === now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const isYesterday = date.toDateString() === yesterday.toDateString()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000))

  if (isToday) return `${date.getHours() >= 18 ? 'Tonight' : 'Today'} at ${timeStr}`
  if (isYesterday) return `Yesterday at ${timeStr}`
  if (diffDays < 7) return `${date.toLocaleDateString('en-US', { weekday: 'long' })} at ${timeStr}`
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at ${timeStr}`
}

/** Format milliseconds as "M:SS" countdown string. */
export function formatCountdown(ms: number): string {
  if (ms <= 0) return '0:00'
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/** Format a date as "Jan 15, 3:00 PM". */
export function formatShortDateTime(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ', ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

/**
 * Format a future timestamp relative to now.
 * >= 4h → date+time string, 2–4h → "Xh", < 2h → "Xh Ym" / "Xm".
 */
export function formatTimeUntilStart(ms: number, startDate: Date): { label: string; isDate: boolean } {
  if (ms <= 0) return { label: 'now', isDate: false }
  const minutes = Math.floor(ms / 60_000)
  const hours = Math.floor(minutes / 60)

  if (hours >= 4) {
    return { label: formatShortDateTime(startDate), isDate: true }
  }
  if (hours >= 2) {
    return { label: `${hours}h`, isDate: false }
  }
  const remainingMin = minutes % 60
  if (hours >= 1) {
    return { label: remainingMin > 0 ? `${hours}h ${remainingMin}m` : `${hours}h`, isDate: false }
  }
  return { label: `${minutes}m`, isDate: false }
}

/**
 * Formats a 'YYYY-MM-DD' date string for display.
 * @param format - 'long' for a full written date, 'short' for a compact slash date (default: 'long')
 * @returns formatted date string
 * @example
 * formatIsoDateString('2026-07-18') // 'July 18, 2026'
 * formatIsoDateString('2026-07-18', 'short') // '07/18/26'
 */
export function formatIsoDateString(dateStr: string, format: 'long' | 'short' = 'long'): string {
  const [year, month, day] = dateStr.split('-')
  if (format === 'short') {
    return `${month}/${day}/${year.slice(2)}`
  }
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}
